/**
 * useTravelManagement Hook
 * Phase 3 Refactor: Extracted from AvailabilityWeeklyDetail.js
 *
 * Manages all travel period state and operations:
 * - travelPeriods: List of all travel periods
 * - activeTravelFilter: Currently selected travel filter
 * - Travel period CRUD operations (create, update, delete)
 * - Overlap detection and warning modals
 * - Travel period day filtering
 */

import { useState, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";

/**
 * Custom hook for managing travel periods and filters
 *
 * @param {Object} params
 * @param {Object} params.availabilityApi - API client for availability operations
 * @param {Object} params.user - Current user object with organisationId
 * @param {Function} params.setSuccessMessage - Callback to show success messages
 * @param {Function} params.setError - Callback to show error messages
 * @returns {Object} Travel management state and operations
 */
const useTravelManagement = ({ availabilityApi, bookingApi, user, setSuccessMessage, setError }) => {
    // Core travel state
    const [travelPeriods, setTravelPeriods] = useState([]);
    const [activeTravelFilter, setActiveTravelFilter] = useState(null);

    // Modal state
    const [showTravelModal, setShowTravelModal] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState(null);
    const [showOverlapWarning, setShowOverlapWarning] = useState(false);
    const [overlappingPeriods, setOverlappingPeriods] = useState([]);
    const [pendingTravelPeriod, setPendingTravelPeriod] = useState(null);
    const [showDeleteTravelModal, setShowDeleteTravelModal] = useState(false);
    const [periodToDelete, setPeriodToDelete] = useState(null);
    const [homeBookingConflicts, setHomeBookingConflicts] = useState([]);
    const [showHomeBookingConflict, setShowHomeBookingConflict] = useState(false);
    // Stores the travelPeriod data so we can resume the save after conflict cancellation
    const [pendingConflictPeriod, setPendingConflictPeriod] = useState(null);

    // Currency unlock (AC9): cancel a locked travel period's bookings from the edit modal.
    // Lifted here so the cancel modal renders as a SIBLING of TravelPeriodModal.
    const [unlockBookings, setUnlockBookings] = useState([]);
    const [showUnlockCancelModal, setShowUnlockCancelModal] = useState(false);
    const [isUnlockProcessing, setIsUnlockProcessing] = useState(false);
    // Bumped after an unlock cancel so TravelPeriodModal re-fetches its lock state.
    const [lockRefreshKey, setLockRefreshKey] = useState(0);
    const [isConflictCancelProcessing, setIsConflictCancelProcessing] = useState(false);

    // Cancel-and-delete modal state
    const [affectedBookings, setAffectedBookings] = useState([]);
    const [isCancelDeleteProcessing, setIsCancelDeleteProcessing] = useState(false);
    const [showCancelTravelModal, setShowCancelTravelModal] = useState(false);

    // Past periods toggle
    const [showPastPeriods, setShowPastPeriods] = useState(false);

    // Availability setup modal state (auto-opens after creating travel period)
    const [showAvailabilitySetupModal, setShowAvailabilitySetupModal] = useState(false);
    const [availabilitySetupPeriod, setAvailabilitySetupPeriod] = useState(null);

    /**
     * Check if travel period overlaps with existing periods
     */
    const checkTravelPeriodOverlap = useCallback(
        (newPeriod, excludePeriod = null) => {
            const newStart = newPeriod.startDate.slice(0, 10);
            const newEnd = newPeriod.endDate.slice(0, 10);

            return travelPeriods
                .map((period, index) => ({ ...period, index }))
                .filter(period => {
                    if (excludePeriod) {
                        // Primary: match by id
                        if (period.id && excludePeriod.id && period.id === excludePeriod.id) return false;
                        // Fallback: match by original dates (guards against id mismatch timing issues)
                        if (
                            period.startDate?.slice(0, 10) === excludePeriod.startDate?.slice(0, 10) &&
                            period.endDate?.slice(0, 10) === excludePeriod.endDate?.slice(0, 10)
                        ) return false;
                    }
                    const existingStart = period.startDate.slice(0, 10);
                    const existingEnd = period.endDate.slice(0, 10);
                    return newStart <= existingEnd && newEnd >= existingStart;
                });
        },
        [travelPeriods]
    );

    /**
     * Save travel period (create or update)
     */
    const handleSaveTravelPeriod = useCallback(
        async (travelPeriod, { skipConflictCheck = false } = {}) => {
            try {
                const isEditMode = editingPeriod && editingPeriod.id;
                const editingId = editingPeriod?.id;

                // Check for overlaps (skip if this is a confirmed save from overlap warning)
                // When editing, exclude the period being edited from overlap check
                if (!pendingTravelPeriod) {
                    const overlapping = checkTravelPeriodOverlap(
                        travelPeriod,
                        isEditMode ? editingPeriod : null
                    );

                    if (overlapping.length > 0) {
                        setPendingTravelPeriod(travelPeriod);
                        setOverlappingPeriods(overlapping);
                        setShowOverlapWarning(true);
                        return; // Don't save yet, wait for user confirmation
                    }
                }

                let savedPeriod = null;
                if (isEditMode) {
                    // Any booking in the new dates that isn't this trip's own is a clash.
                    const homeConflicts = skipConflictCheck ? [] : await availabilityApi.getHomeBookingsInDateRange(
                        travelPeriod.startDate,
                        travelPeriod.endDate,
                        editingId
                    );
                    if (homeConflicts.length > 0) {
                        setHomeBookingConflicts(homeConflicts);
                        setPendingConflictPeriod(travelPeriod);
                        setShowHomeBookingConflict(true);
                        return;
                    }

                    // Update existing travel period (using contexts API)
                    console.log("Updating travel period with ID:", editingId);
                    const updateResponse = await availabilityApi.updateContext(editingId, travelPeriod);
                    savedPeriod = { ...travelPeriod, id: editingId }; // For editing, ID already exists

                    // Warn if date change caused existing slots to be removed
                    if (updateResponse?.slotsRemoved > 0) {
                        toast.warning(
                            `Travel period updated. ${updateResponse.slotsRemoved} slot${updateResponse.slotsRemoved !== 1 ? 's' : ''} from the old dates were removed — you'll need to add slots for the new dates.`,
                            { autoClose: 6000 }
                        );
                    }
                } else {
                    // Any active booking in these dates (home, another trip, anything) is a clash.
                    const homeConflicts = skipConflictCheck ? [] : await availabilityApi.getHomeBookingsInDateRange(
                        travelPeriod.startDate,
                        travelPeriod.endDate
                    );
                    if (homeConflicts.length > 0) {
                        setHomeBookingConflicts(homeConflicts);
                        setPendingConflictPeriod(travelPeriod);
                        setShowHomeBookingConflict(true);
                        setPendingTravelPeriod(null);
                        return;
                    }

                    // Create new travel period.
                    // defaultLocationId must be passed through (the modal's
                    // venue dropdown sets it). Earlier versions of this payload
                    // dropped it, leaving travel periods with no default
                    // location even after the user picked one.
                    // privatePrice/currency carry the optional travel-period lesson
                    // rate (the backend persists them on availability_contexts and
                    // applies the rate to bookings inside the period).
                    const contextData = {
                        startDate: travelPeriod.startDate,
                        endDate: travelPeriod.endDate,
                        context: "travel",
                        mode: "open",
                        reason: `Teaching at ${travelPeriod.destination}`,
                        destination: travelPeriod.destination,
                        location: travelPeriod.location,
                        defaultLocationId: travelPeriod.defaultLocationId ?? null,
                        privatePrice: travelPeriod.privatePrice,
                        currency: travelPeriod.currency,
                    };
                    const response = await availabilityApi.createContext(contextData);
                    savedPeriod = response; // Backend returns the created period with ID

                    // NEW: Auto-open availability setup modal for new periods

                    // Close the travel period modal first
                    setShowTravelModal(false);
                    setEditingPeriod(null);

                    // Then open availability setup modal
                    setShowAvailabilitySetupModal(true);
                    setAvailabilitySetupPeriod(savedPeriod);
                }

                // Reload all travel periods
                const updatedPeriods = await availabilityApi.getTravelPeriods(user.organisationId);
                setTravelPeriods(updatedPeriods);

                setSuccessMessage(
                    isEditMode ? "Travel period updated successfully!" : "Travel period created successfully!"
                );

                // Clear all modal state
                setShowTravelModal(false);
                setPendingTravelPeriod(null);
                setShowOverlapWarning(false);
                setOverlappingPeriods([]);
                setEditingPeriod(null);

                // Return the saved period with its ID
                return savedPeriod;
            } catch (error) {
                console.error("Error saving travel period:", error);

                // A booking landed after the modal opened: the modal shows the lock itself
                // (toast + locked picker), so no page banner and no state reset here.
                if (error.errorCode === "TRAVEL_CURRENCY_LOCKED") {
                    throw error;
                }

                // The shared client's wrappers throw a plain Error carrying the backend
                // message in error.message; error.response is always undefined here.
                const errorMessage = error.message || "Failed to save travel period. Please try again.";

                setError(errorMessage);
                setTimeout(() => setError(""), 5000);

                // Clear pending state on error
                setPendingTravelPeriod(null);
                setShowOverlapWarning(false);
                setOverlappingPeriods([]);

                throw error;
            }
        },
        [
            editingPeriod,
            pendingTravelPeriod,
            checkTravelPeriodOverlap,
            availabilityApi,
            user,
            setSuccessMessage,
            setError,
        ]
    );

    /**
     * Confirm overlap and proceed with save
     */
    const handleConfirmOverlap = useCallback(async () => {
        // User confirmed they're aware of the overlap
        if (pendingTravelPeriod) {
            await handleSaveTravelPeriod(pendingTravelPeriod);
        }
    }, [pendingTravelPeriod, handleSaveTravelPeriod]);

    /**
     * Cancel overlap warning
     */
    const handleCancelOverlap = useCallback(() => {
        setPendingTravelPeriod(null);
        setShowOverlapWarning(false);
        setOverlappingPeriods([]);
    }, []);

    /**
     * Issue the Refund/Credit decisions for paid bookings (CancelTravelPeriodModal), the
     * same way the sick-day flow does: a full return (no amount) cancels the booking, so
     * these never go through bulk-cancel. Returns { issued, failed }.
     */
    const issuePaidReturns = useCallback(async (paidDecisions, message) => {
        let issued = 0;
        let failed = 0;
        for (const decision of paidDecisions) {
            try {
                await bookingApi.issueReturn(decision.bookingId, { type: decision.action, message });
                issued += 1;
            } catch (err) {
                console.error(`Travel cancel: failed to ${decision.action} booking ${decision.bookingId}:`, err);
                failed += 1;
            }
        }
        return { issued, failed };
    }, [bookingApi]);

    /** Summary toast shared by the travel cancel flows. */
    const cancelSummary = (cancelled, returns) => {
        const parts = [];
        if (cancelled > 0) parts.push(`${cancelled} booking${cancelled !== 1 ? 's' : ''} cancelled`);
        if (returns.issued > 0) parts.push(`${returns.issued} refund/credit issued`);
        return parts.join(", ");
    };

    /**
     * Confirm conflict modal: cancel affected bookings then resume the blocked save.
     * Create path: cancels each booking individually (no contextId yet).
     * Edit path: uses bulk-cancel-bookings endpoint with contextId.
     */
    const handleConfirmConflictCancel = useCallback(async (message, bookingIds, paidDecisions = []) => {
        setIsConflictCancelProcessing(true);
        try {
            const isEditMode = editingPeriod && editingPeriod.id;

            // Paid bookings first: refund or credit (which cancels them).
            const returns = await issuePaidReturns(paidDecisions, message);
            if (returns.failed > 0) {
                toast.error(`${returns.failed} refund/credit failed. Resolve those bookings individually, then try again.`);
                return;
            }

            if (bookingIds.length > 0) {
                if (isEditMode) {
                    // Edit path: contextId is known — use the dedicated bulk-cancel endpoint
                    await availabilityApi.bulkCancelTravelBookings(editingPeriod.id, bookingIds, message);
                } else {
                    // Create path: no contextId yet — cancel each booking individually
                    await Promise.all(
                        bookingIds.map(id => bookingApi.cancelBooking(id, message))
                    );
                }
            }

            // Clear conflict state
            setShowHomeBookingConflict(false);
            setHomeBookingConflicts([]);
            toast.success(`${cancelSummary(bookingIds.length, returns)}. Saving travel period...`);

            // Resume the blocked save
            const periodToResume = pendingConflictPeriod;
            setPendingConflictPeriod(null);
            if (periodToResume) {
                await handleSaveTravelPeriod(periodToResume);
            }
        } catch (err) {
            // A booking was paid after the list loaded: show it in the paid step instead.
            if (err.errorCode === 'PAID_BOOKING_REQUIRES_RETURN' && pendingConflictPeriod) {
                try {
                    const fresh = await availabilityApi.getHomeBookingsInDateRange(
                        pendingConflictPeriod.startDate, pendingConflictPeriod.endDate, editingPeriod?.id || null
                    );
                    setHomeBookingConflicts(fresh);
                } catch { /* keep the stale list; the toast below explains */ }
            }
            toast.error(err.message || 'Failed to cancel bookings. Please try again.');
        } finally {
            setIsConflictCancelProcessing(false);
        }
    }, [editingPeriod, availabilityApi, bookingApi, pendingConflictPeriod, handleSaveTravelPeriod, issuePaidReturns]);

    /**
     * Keep the clashing bookings and save the travel period anyway. They stay booked, and
     * Set Travel Availability leaves those times out of the trip's open availability.
     */
    const handleKeepConflicts = useCallback(async () => {
        const periodToResume = pendingConflictPeriod;
        setShowHomeBookingConflict(false);
        setHomeBookingConflicts([]);
        setPendingConflictPeriod(null);
        if (periodToResume) {
            try {
                await handleSaveTravelPeriod(periodToResume, { skipConflictCheck: true });
            } catch { /* handleSaveTravelPeriod already reported the error */ }
        }
    }, [pendingConflictPeriod, handleSaveTravelPeriod]);

    /**
     * Dismiss conflict modal without cancelling — instructor goes back to fix dates manually.
     */
    const handleCloseConflictModal = useCallback(() => {
        if (!isConflictCancelProcessing) {
            setShowHomeBookingConflict(false);
            setHomeBookingConflicts([]);
            setPendingConflictPeriod(null);
        }
    }, [isConflictCancelProcessing]);

    /**
     * Get travel periods that include a specific day
     */
    const getTravelPeriodsForDay = useCallback(
        day => {
            return travelPeriods.filter(period => {
                const periodStart = DateTime.fromISO(period.startDate);
                const periodEnd = DateTime.fromISO(period.endDate);

                // Check if day is within this travel period
                return day >= periodStart.startOf("day") && day <= periodEnd.endOf("day");
            });
        },
        [travelPeriods]
    );

    /**
     * Handle clicking on a calendar day with travel period
     */
    const handleDayTravelClick = useCallback((day, travelPeriodsForDay) => {
        if (travelPeriodsForDay.length === 0) return;

        // If only one period, filter to it
        if (travelPeriodsForDay.length === 1) {
            setActiveTravelFilter(travelPeriodsForDay[0]);
        } else {
            // Multiple periods - could show a selector or cycle through them
            // For now, use the first one
            setActiveTravelFilter(travelPeriodsForDay[0]);
        }
    }, []);

    /**
     * Clear travel filter
     */
    const handleClearTravelFilter = useCallback(() => {
        setActiveTravelFilter(null);
    }, []);

    /**
     * Show delete confirmation: fetches affected bookings first.
     * - 0 bookings → simple confirm modal
     * - N bookings → cancel-and-delete modal
     */
    const handleDeleteTravelPeriod = useCallback(async (periodId) => {
        setPeriodToDelete(periodId);
        try {
            const bookings = await availabilityApi.getTravelPeriodBookings(periodId);
            setAffectedBookings(bookings);
            if (bookings.length === 0) {
                // No active bookings — use existing simple confirm modal
                setShowDeleteTravelModal(true);
            } else {
                // Active bookings exist — open the cancel-and-delete modal
                setShowCancelTravelModal(true);
            }
        } catch (err) {
            toast.error(err.message || 'Failed to check bookings for this travel period.');
        }
    }, [availabilityApi]);

    /**
     * Confirm cancel-and-delete via the CancelTravelPeriodModal
     */
    const handleConfirmCancelAndDelete = useCallback(async (message, bookingIds, paidDecisions = []) => {
        const periodId = periodToDelete;
        setIsCancelDeleteProcessing(true);
        try {
            // Paid bookings first: refund or credit (which cancels them). The backend
            // refuses to delete a period that still holds a paid booking.
            const returns = await issuePaidReturns(paidDecisions, message);
            if (returns.failed > 0) {
                toast.error(`${returns.failed} refund/credit failed. Resolve those bookings individually, then try again.`);
                return;
            }
            if (activeTravelFilter && activeTravelFilter.id === periodId) {
                setActiveTravelFilter(null);
            }
            await availabilityApi.cancelAndDeleteTravelPeriod(periodId, bookingIds, message);
            setShowCancelTravelModal(false);
            setPeriodToDelete(null);
            setAffectedBookings([]);

            // Reload travel periods
            try {
                const updatedPeriods = await availabilityApi.getTravelPeriods(user.organisationId);
                setTravelPeriods(updatedPeriods);
            } catch {
                setTravelPeriods([]);
            }
            toast.success('Travel period deleted successfully!');
        } catch (err) {
            // A booking was paid after the list loaded: re-fetch so it shows in the paid step.
            if (err.errorCode === 'PAID_BOOKING_REQUIRES_RETURN') {
                try {
                    setAffectedBookings(await availabilityApi.getTravelPeriodBookings(periodId));
                } catch { /* keep the stale list; the toast below explains */ }
            }
            toast.error(err.message || 'Failed to cancel and delete travel period.');
        } finally {
            setIsCancelDeleteProcessing(false);
        }
    }, [periodToDelete, activeTravelFilter, availabilityApi, user, setTravelPeriods, issuePaidReturns]);

    /** Open the cancel modal for a locked period's bookings (from TravelPeriodModal). */
    const openUnlockCancel = useCallback((bookings) => {
        setUnlockBookings(bookings);
        setShowUnlockCancelModal(true);
    }, []);

    const closeUnlockCancel = useCallback(() => {
        if (!isUnlockProcessing) {
            setShowUnlockCancelModal(false);
            setUnlockBookings([]);
        }
    }, [isUnlockProcessing]);

    /**
     * Cancel a locked period's bookings so its currency can change: refund/credit the paid
     * ones (which cancels them), bulk-cancel the unpaid ones. The edit modal stays open
     * and re-fetches its lock state; at 0 bookings the currency picker unlocks in place.
     */
    const handleConfirmUnlockCancel = useCallback(async (message, bookingIds, paidDecisions = []) => {
        if (!editingPeriod?.id) return;
        setIsUnlockProcessing(true);
        try {
            const returns = await issuePaidReturns(paidDecisions, message);
            if (returns.failed > 0) {
                toast.error(`${returns.failed} refund/credit failed. Resolve those bookings individually, then try again.`);
                setLockRefreshKey(k => k + 1);
                return;
            }
            if (bookingIds.length > 0) {
                await availabilityApi.bulkCancelTravelBookings(editingPeriod.id, bookingIds, message);
            }
            setShowUnlockCancelModal(false);
            setUnlockBookings([]);
            toast.success(`${cancelSummary(bookingIds.length, returns)}. You can now change this trip's currency.`);
        } catch (err) {
            if (err.errorCode === 'PAID_BOOKING_REQUIRES_RETURN') {
                try {
                    setUnlockBookings(await availabilityApi.getTravelPeriodBookings(editingPeriod.id));
                } catch { /* keep the stale list; the toast below explains */ }
            }
            toast.error(err.message || 'Failed to cancel bookings. Please try again.');
        } finally {
            setIsUnlockProcessing(false);
            setLockRefreshKey(k => k + 1);
        }
    }, [editingPeriod, availabilityApi, issuePaidReturns]);

    /**
     * Close the cancel-and-delete modal without any action
     */
    const handleCloseCancelTravelModal = useCallback(() => {
        if (!isCancelDeleteProcessing) {
            setShowCancelTravelModal(false);
            setPeriodToDelete(null);
            setAffectedBookings([]);
        }
    }, [isCancelDeleteProcessing]);

    /**
     * Confirm and execute travel period deletion
     */
    const confirmDeleteTravelPeriod = useCallback(async () => {
        const periodId = periodToDelete;
        setShowDeleteTravelModal(false);
        setPeriodToDelete(null);

        try {
            console.log("Deleting travel period with ID:", periodId);

            // Clear active filter if deleting the currently selected period
            if (activeTravelFilter && activeTravelFilter.id === periodId) {
                setActiveTravelFilter(null);
            }

            // Delete travel period using contexts API
            await availabilityApi.deleteContext(periodId);

            // Reload travel periods
            try {
                const updatedPeriods = await availabilityApi.getTravelPeriods(user.organisationId);
                setTravelPeriods(updatedPeriods);
            } catch (loadError) {
                console.warn("No travel periods found after delete:", loadError);
                setTravelPeriods([]);
            }

            toast.success("Travel period deleted successfully!");
        } catch (error) {
            console.error("Error deleting travel period:", error);
            toast.error(error.message || "Failed to delete travel period. Please try again.");
        }
    }, [periodToDelete, activeTravelFilter, availabilityApi, user]);

    /**
     * Cancel travel period deletion
     */
    const cancelDeleteTravelPeriod = useCallback(() => {
        setShowDeleteTravelModal(false);
        setPeriodToDelete(null);
    }, []);

    /**
     * Open travel modal for creating new period
     */
    const openTravelModal = useCallback(() => {
        setEditingPeriod(null);
        setShowTravelModal(true);
    }, []);

    /**
     * Open travel modal for editing existing period
     */
    const openTravelModalForEdit = useCallback(period => {
        setEditingPeriod(period);
        setShowTravelModal(true);
    }, []);

    /**
     * Close travel modal
     */
    const closeTravelModal = useCallback(() => {
        setShowTravelModal(false);
        setEditingPeriod(null);
    }, []);

    // Filter travel periods by visibility (hide past periods by default)
    const visibleTravelPeriods = useMemo(() => {
        const today = DateTime.local().startOf('day');
        const upcoming = travelPeriods
            .filter(p => DateTime.fromISO(p.endDate) >= today)
            .sort((a, b) => DateTime.fromISO(a.startDate) - DateTime.fromISO(b.startDate));
        if (!showPastPeriods) return upcoming;
        const past = travelPeriods
            .filter(p => DateTime.fromISO(p.endDate) < today)
            .sort((a, b) => DateTime.fromISO(b.startDate) - DateTime.fromISO(a.startDate));
        return [...upcoming, ...past];
    }, [travelPeriods, showPastPeriods]);

    // Memoize the return object to prevent creating new references on every render
    return useMemo(() => ({
        // State
        travelPeriods,
        activeTravelFilter,
        showTravelModal,
        editingPeriod,
        showOverlapWarning,
        overlappingPeriods,
        pendingTravelPeriod,
        showDeleteTravelModal,
        periodToDelete,
        showAvailabilitySetupModal,
        availabilitySetupPeriod,

        // Operations
        handleSaveTravelPeriod,
        handleConfirmOverlap,
        handleCancelOverlap,
        getTravelPeriodsForDay,
        handleDayTravelClick,
        handleClearTravelFilter,
        handleDeleteTravelPeriod,
        handleConfirmCancelAndDelete,
        handleCloseCancelTravelModal,
        confirmDeleteTravelPeriod,
        cancelDeleteTravelPeriod,
        openTravelModal,
        openTravelModalForEdit,
        closeTravelModal,

        // Cancel-and-delete modal
        affectedBookings,
        isCancelDeleteProcessing,
        showCancelTravelModal,

        // Home booking conflict (pre-save gate)
        homeBookingConflicts,
        showHomeBookingConflict,
        isConflictCancelProcessing,
        handleConfirmConflictCancel,
        handleCloseConflictModal,
        handleKeepConflicts,

        // Currency unlock (cancel a locked period's bookings)
        unlockBookings,
        showUnlockCancelModal,
        isUnlockProcessing,
        lockRefreshKey,
        openUnlockCancel,
        closeUnlockCancel,
        handleConfirmUnlockCancel,

        // Past periods toggle
        showPastPeriods,
        setShowPastPeriods,
        visibleTravelPeriods,

        // Direct state setters
        setTravelPeriods,
        setActiveTravelFilter,
        setPendingTravelPeriod,
        setShowOverlapWarning,
        setOverlappingPeriods,
        setEditingPeriod,
        setShowTravelModal,
        setShowDeleteTravelModal,
        setPeriodToDelete,
        setShowAvailabilitySetupModal,
        setAvailabilitySetupPeriod,
    }), [
        travelPeriods,
        activeTravelFilter,
        showTravelModal,
        editingPeriod,
        showOverlapWarning,
        overlappingPeriods,
        pendingTravelPeriod,
        showDeleteTravelModal,
        periodToDelete,
        showAvailabilitySetupModal,
        availabilitySetupPeriod,
        handleSaveTravelPeriod,
        handleConfirmOverlap,
        handleCancelOverlap,
        getTravelPeriodsForDay,
        handleDayTravelClick,
        handleClearTravelFilter,
        handleDeleteTravelPeriod,
        handleConfirmCancelAndDelete,
        handleCloseCancelTravelModal,
        confirmDeleteTravelPeriod,
        cancelDeleteTravelPeriod,
        openTravelModal,
        openTravelModalForEdit,
        closeTravelModal,
        affectedBookings,
        isCancelDeleteProcessing,
        showCancelTravelModal,
        showPastPeriods,
        visibleTravelPeriods,
        unlockBookings,
        showUnlockCancelModal,
        isUnlockProcessing,
        lockRefreshKey,
        openUnlockCancel,
        closeUnlockCancel,
        handleConfirmUnlockCancel,
        homeBookingConflicts,
        showHomeBookingConflict,
        isConflictCancelProcessing,
        handleConfirmConflictCancel,
        handleCloseConflictModal,
        handleKeepConflicts,
    ]);
};

export default useTravelManagement;
